import AddIcon from '@mui/icons-material/Add'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import { Avatar, Box, List, ListItem, Menu, MenuItem, Typography } from '@mui/material'
import { styled } from '@mui/system'
import { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import * as routes from '../constants/routes'
import { useUsersContext } from '../hooks/useUsers'
import { useWorkspaceContext } from '../hooks/useWorkspace'
import { ReactComponent as IconSettings } from '../icons/settings.svg'

const SidebarNodeMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    width: '260px',
    border: '1px solid',
    borderColor: theme.palette.grey[100],
    borderRadius: '6px',
    boxShadow: '0 4px 6px -2px rgba(16, 24, 40, 0.03), 0 12px 16px -4px rgba(16, 24, 40, 0.08)',
  },
  '& ul': {
    padding: 0,
  },
})) as typeof Menu

interface IProps {
  isSidebarOpen?: boolean
}

export default function (props: IProps) {
  const { isSidebarOpen } = props

  const [anchorEl, setAnchorEl] = useState(null)

  const navigate = useNavigate()
  const location = useLocation()

  const {
    selectedWorkspaceDetails,
    workspaces,
    onWorkspaceChange,
    selectedNodeId,
    setIsCreateAdditionalWorkspaceModal,
  } = useWorkspaceContext()
  const { isWorkspaceOwner } = useUsersContext()
  const selectedDetails = selectedWorkspaceDetails

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }
  const workspaceId = selectedNodeId

  const filteredWorkspaces = useMemo(
    () =>
      workspaces
        .sort((prevDate, nextDate) => {
          const prevCreatedAt = prevDate.createdAt ? new Date(prevDate.createdAt).getTime() : 0
          const nextCreatedAt = nextDate.createdAt ? new Date(nextDate.createdAt).getTime() : 0
          return nextCreatedAt - prevCreatedAt
        })
        .filter((workspace) => {
          const workspaceId = workspace.id?.toString()

          return workspaceId !== undefined && selectedNodeId !== workspaceId && workspaces.length > 1
        }),
    [workspaces, selectedNodeId],
  )

  return (
    <Box
      sx={{
        position: 'relative',
        padding: isSidebarOpen ? 1.5 : '',
        paddingTop: 1.5,
        borderTop: '1px solid',
        borderTopColor: 'grey.800',
      }}
    >
      <SidebarNodeMenu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableAutoFocusItem
        anchorOrigin={{
          vertical: isSidebarOpen ? 'top' : 'bottom',
          horizontal: isSidebarOpen ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <ListItem
          disablePadding
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            paddingY: 1,
            borderBottom: '1px solid',
            borderBottomColor: 'grey.100',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 0.5, paddingY: 1, paddingX: 2.25 }}>
            <Avatar
              variant="rounded"
              alt={selectedWorkspaceDetails?.name}
              src={selectedWorkspaceDetails?.avatar?.url}
              sx={{
                position: 'relative',
                height: '40px',
                width: '40px',
                color: 'primary.600',
                backgroundColor: 'primary.50',
                '& svg': {
                  display: 'none',
                },
              }}
            >
              {selectedWorkspaceDetails?.name?.substring(0, 1)}
            </Avatar>

            <Box sx={{ display: 'flex', flexDirection: 'column', marginLeft: 1.5 }}>
              <Typography variant="h5" color="grey.900">
                {selectedWorkspaceDetails.name}
              </Typography>

              <Typography variant="caption2" color="grey.450">
                Workspace
              </Typography>
            </Box>
          </Box>

          <List disablePadding sx={{ width: '100%' }}>
            <MenuItem
              onClick={() => {
                if (isWorkspaceOwner) {
                  navigate(`/${workspaceId}/workspace/settings`)
                } else {
                  navigate(`/${workspaceId}/workspace/users`)
                }
                handleClose()
              }}
            >
              <IconSettings style={{ width: '16px', height: '16px', fill: '#a3a3a3' }} />

              <Typography variant="body2" color="grey.700" ml={1.5}>
                Workspace settings
              </Typography>
            </MenuItem>
          </List>
        </ListItem>

        {filteredWorkspaces.length > 0 && (
          <ListItem
            disablePadding
            sx={{
              borderBottom: '1px solid',
              borderBottomColor: 'grey.100',
            }}
          >
            <Box sx={{ width: '100%', marginTop: 0.5, marginBottom: 1 }}>
              <Typography variant="caption" color="grey.450" py={0.75} px={2.5}>
                Switch workspace
              </Typography>

              <List
                disablePadding
                sx={{
                  overflowY: 'auto',
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  overflowY: 'overlay',
                  width: '100%',
                  maxHeight: '120px',
                }}
              >
                {filteredWorkspaces.map((workspace) => (
                  <MenuItem
                    onClick={() => {
                      onWorkspaceChange(String(workspace.id))
                      handleClose()
                    }}
                    key={workspace.id}
                    sx={{
                      paddingY: 1,
                      paddingX: 2.25,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        variant="rounded"
                        alt={workspace?.name}
                        src={workspace?.avatar?.url}
                        sx={{
                          position: 'relative',
                          height: '24px',
                          width: '24px',
                          color: 'primary.600',
                          backgroundColor: 'primary.50',
                          '& svg': {
                            display: 'none',
                          },
                        }}
                      >
                        {workspace?.name?.substring(0, 1)}
                      </Avatar>

                      <Typography variant="h6" color="grey.900" ml={1.5}>
                        {workspace.name}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </List>
            </Box>
          </ListItem>
        )}

        <MenuItem
          onClick={() => {
            setIsCreateAdditionalWorkspaceModal(true)
            handleClose()
          }}
          sx={{
            marginTop: 0.5,
            marginBottom: 1,
            paddingY: 1.25,
            paddingX: 2,
          }}
        >
          <AddIcon sx={{ width: '16px', height: '16px', fill: '#a3a3a3' }} />

          <Typography variant="body2" color="grey.700" ml={1.5}>
            Create a new workspace
          </Typography>
        </MenuItem>
      </SidebarNodeMenu>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: 1,
          cursor: 'pointer',
          borderRadius: '6px',
          backgroundColor:
            Boolean(anchorEl) || location.pathname === routes.workspaceRoute.replace(':workspaceId', selectedNodeId)
              ? 'rgba(41, 41, 41, 0.36)'
              : '',
          '&:hover': {
            backgroundColor: 'rgba(41, 41, 41, 0.36)',
          },
        }}
        onClick={handleClick}
      >
        <Avatar
          variant="rounded"
          alt={selectedDetails?.name}
          src={selectedDetails?.avatar?.url}
          sx={{
            position: 'relative',
            height: '32px',
            width: '32px',
            color: 'primary.600',
            backgroundColor: 'primary.50',
            '& svg': {
              display: 'none',
            },
          }}
        >
          {selectedDetails?.name?.substring(0, 1)}
        </Avatar>

        {isSidebarOpen && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '115px', marginX: 1.5 }}>
              <Typography variant="body2" color="grey.300" sx={{ fontWeight: 600, lineHeight: 1.43 }}>
                {selectedDetails?.name ? `${selectedDetails.name.slice(0, 10)}...` : ''}
              </Typography>

              <Typography variant="caption" color="grey.450">
                Workspace
              </Typography>
            </Box>

            <UnfoldMoreIcon sx={{ width: '24px', height: '24px', fill: '#737373' }} />
          </>
        )}
      </Box>
    </Box>
  )
}
